/* eslint-disable import/no-unresolved */
// 加载基础框架
import Vue from 'vue';
import Router from 'vue-router';
// 公共基础模块
import Ctx from './modules/ctx';
import Log from './modules/log';
import Fetch from './modules/fetch';
import ABTest from './modules/abtest';
import Bridge from './modules/bridge';
import Resources from './modules/resources';
import Theme from './modules/theme';
import toast from './components/toast';
import { elementPath, getProp, htmlEncode } from './modules/utils';

// #region 基础逻辑函数
/**
 * 设置组件识别名
 */
function setComponentName(vue, name) {
  if (name && vue.$el?.setAttribute) {
    vue.$el.setAttribute('data-cname', name ?? vue.$vnode?.componentOptions?.tag ?? '');
  }
}

/**
 * 根据viewType自动判断返回pc/m/pad的视图
 */
function getViewPath(route, ctx) {
  let view = route.view;
  if (typeof view === 'function') {
    view = view(ctx);
  } else if (ctx.view === ctx.VIEW.MO && route.mview) {
    view = route.mview;
  } else if (ctx.view === ctx.VIEW.PAD && route.padview) {
    view = route.padview;
  }
  return view?.replace(/^\//, '');
}

/**
 * 预加载页面
 */
export function asyncLoadPage(route, isPreload, preloadArgs, $hobbit) {
  // 路由名或路由对象自动转换
  if (typeof route === 'string') {
    route = $hobbit.$config.routes[route];
  }

  // 无效路由
  if (!route) return null;

  return import(
    /* webpackChunkName: "[request]" */
    /* webpackInclude: /\.vue$/ */
    /* webpackExclude: /\/components\// */
    `@/views/${getViewPath(route, $hobbit.$ctx)}`
  ).then(chunk => {
    chunk.default.isHobbitPage = true;
    // 预加载的模块，不再链式触发下一级预加载，异步执行预热，返回chunk
    if (isPreload) {
      // 调用预热钩子
      if (typeof chunk?.default?.preload === 'function') {
        chunk.default.preloadPromise = new Promise((resolve, reject) => {
          chunk.default.preload.call(
            chunk.default.methods,
            resolve,
            reject,
            preloadArgs,
          );
        });
      }
      return chunk;
    }

    // 该路由页面有预加载配置，继续异步加载预加载模块
    if (Array.isArray(route.preloads)) {
      $hobbit.$ctx.onload.then(() => {
        for (const plName of route.preloads) {
          asyncLoadPage(plName, true, preloadArgs, $hobbit);
        }
      });
    }

    // 等待当前语言资源和主题配置已经安装好才继续挂载页面组件，避免页面执行时无多语言资源
    return Promise.all([
      $hobbit.$resources.$installer.install(route.revertLangOption?.[$hobbit.$ctx.lang] ?? $hobbit.$ctx.lang),
      $hobbit.$theme.$installer.install($hobbit.$ctx.biz),
    ]).then(() => chunk).catch(() => chunk);
  });
}

/**
 * 路由适配器
 * webpack参考：https://webpack.docschina.org/api/module-methods/#magic-comments
 */
function routeAdapter(name, route, $hobbit, idx = 1) {
  const result = {
    path: route.path,
    meta: {
      index: route.index ?? idx,
      keepAlive: route.keepAlive ?? false,
      pv: route.pv ?? name ?? false,
      revertLangOption: route.revertLangOption,
      ...route.meta,
    },
    component: () => asyncLoadPage(route, false, null, $hobbit),
  };

  if (name) {
    result.name = name;
  }

  if (route.redirect) {
    result.redirect = route.redirect;
  }

  if (route.children) {
    result.children = route.children.map(child => routeAdapter(child.name, child, $hobbit));
  }

  return result;
}

/**
 * 设置meta标签
 */
export function setMeta(name, content) {
  let tag = document.querySelector(`meta[name=${name}]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.name = name;
    document.head.insertBefore(tag, document.head.firstChild);
  }
  tag.setAttribute('content', htmlEncode(content));
}

/**
 * 设置页面SEO关键信息
 */
export function setSEOInfo(title, keywords, description) {
  if (title) document.title = title;
  if (description) setMeta('description', description);
  if (keywords) setMeta('keywords', keywords);
}
// #endregion

/**
 * 页面增强函数【已淘汰】增强能力改用Vue全局引入方式解决。此函数为兼容使用的空函数。如果依然需要“条件KeepAlive”能力，请自行在activated钩子中实现判断缓存是否依然有效+更新数据逻辑
 * 1、自动化页面基础PV
 * 2、处理多语言资源数据res
 * 3、解决KeepAlive问题，如下：
 * 由于Vue自身的钩子函数在KeepAlive、软刷新等情况下存在差异。
 * 因此实现一个Page类，将这些差异抹平。对于业务开发则简单很多，只关心load和render两个钩子即可。
 * #页面进入方式种类：
 * #1、首次进入：着陆页即首个个页面打开的情况（例如：通过浏览器地址栏输入URL回车进入时）
 * #2、应用内跳转进入：通过路由跳转进入
 * #3、软刷新进入：使用app.pageReload()方法重新加载当前页进入
 * #页面DOM缓存类型：
 * #1、开启KeepAlive：启用了页面Dom缓存的情况
 * #2、未开启KeepAlive：未启用页面Dom缓存的情况
 * 针对以上情况的组合，Vue声明周期钩子具体表现为：
 * 1、首次进入+未开启KeepAlive：beforeRouteEnter -> created -> mounted
 * 2、应用内跳转进入+未开启KeepAlive：beforeRouteLeave -> beforeRouteEnter -> created -> mounted
 * 3、软刷新进入+未开启KeepAlive：created -> mounted
 * 4、首次进入+开启KeepAlive：beforeRouteEnter -> created -> mounted -> activated
 * 5、应用内跳转进入+开启KeepAlive：beforeRouteLeave -> beforeRouteEnter -> deactivated -> activated
 * 6、软刷新进入+开启KeepAlive：deactivated -> created -> mounted -> activated
 */
export function hobbitPage(options) {
  return options;
}
// 兼容旧版的别名
export const easyPage = hobbitPage;

/**
 * 组件增强函数【已淘汰】增强能力改用Vue全局引入方式解决。此函数为兼容使用的空函数。
 * 目前仅增加了多语言支持，但为后续AOP留出了接口
 */
export function hobbitComponent(options) {
  return options;
}
// 兼容旧版的别名
export const easyComponent = hobbitComponent;

/**
 * 路由增强函数
 */
export function hobbitRouter($hobbit) {
  // 初始化和加载路由配置
  const routes = [];
  // 添加异步页面组件路由
  let idx = 1;
  for (const name in $hobbit.$config.routes) {
    routes.push(routeAdapter(
      name,
      $hobbit.$config.routes[name],
      $hobbit,
      idx += 1,
    ));
  }

  // 实例化路由模块
  const router = new Router({
    mode: $hobbit.$config.site.routerMode,
    // 站点有二级目录，则需配置上，否则静态资源加载不上
    base: $hobbit.$config.site.routerBase,
    routes,
  });

  // 路由切换即页面切换时，处理基础打点和更新页面切换时间
  router.afterEach((to, from) => {
    $hobbit.$ctx.afterEachPage(to, from);
  });

  return router;
}

/**
 * 创建HobbitApp函数
 */
export function createHobbitApp(
  appView,
  $config,
  extend,
  vueOptions = {},
) {
  // =#=#=#=#=#=#=#=#=#= 基础模块声明 =#=#=#=#=#=#=#=#=#=
  const $ctx = new Ctx($config);
  const $bridge = new Bridge($config, $ctx);
  const $fetch = new Fetch($config, $ctx);
  const $log = new Log($config, $ctx);
  const $resources = new Resources($config, $ctx);
  const $theme = new Theme($config, $ctx);
  const $abtest = new ABTest($config, $ctx);
  // 公共模块全局注册、扩展到Vue实例
  Vue.prototype.setVueGlobalProperty = (name, value) => { Vue.prototype[name] = value; };
  Vue.prototype.$config = $config;
  Vue.prototype.$ctx = $ctx;
  Vue.prototype.$log = $log;
  Vue.prototype.$resources = $resources;
  Vue.prototype.$theme = $theme;
  Vue.prototype.$fetch = $fetch;
  Vue.prototype.$abtest = $abtest;
  Vue.prototype.$bridge = $bridge;

  // 基础实用扩展
  Vue.prototype.$toast = toast;
  Vue.prototype.$get = getProp;
  Vue.prototype.console = window.console;

  // 非生产环境记录组件性能时间线
  Vue.config.performance = !$ctx.isProd();
  // 生产环境禁用启动提示
  Vue.config.productionTip = !$ctx.isProd();
  // 暴露Hobbit全局API
  window.$hobbit = Vue.prototype;

  // =#=#=#=#=#=#=#=#=#= 异常、性能监听初始化 =#=#=#=#=#=#=#=#=#=
  // 全局同步脚本未处理异常监听
  window.addEventListener('error', e => {
    if (!e || typeof e !== 'object') return;

    // 全局错误直接上报
    if (e.error instanceof Error) {
      $log.error(e.error, $log.ERROR_TYPE.JSERROR);
      return;
    }

    // 尝试获取错元素目标
    let target = null;
    let type = $log.ERROR_TYPE.JSERROR;
    const el = e.target || e.srcElement;
    if (el) {
      target = elementPath(el, false);
      const src = el.src || el.href;
      if (src) {
        target = `${target} ${src}`;
        type = $log.ERROR_TYPE.RESOURCE;
      }
    }
    const errorMessageList = [];
    if (e.message) errorMessageList.push(e.message);
    if (e.source) errorMessageList.push(e.source);
    if (e.lineno) errorMessageList.push(`${e.lineno}:${e.colno}`);
    if (target) errorMessageList.push(target);
    $log.error(new Error(errorMessageList.join(' ')), type);
  }, true);
  // 全局Promise未处理异常监听
  window.addEventListener('unhandledrejection', e => {
    if (!e || typeof e !== 'object') return;

    // 错误直接上报
    if (e.reason instanceof Error) {
      $log.error(e.reason, $log.ERROR_TYPE.PROMISE);
      return;
    }

    let target = null;
    const el = e.reason?.target || e.reason?.srcElement;
    if (el) {
      const src = el.src || el.href;
      if (src) {
        // 资源加载错误onerror已经捕获过了
        return;
      }
      target = elementPath(el, false);
    }
    const errorMessageList = [];
    if (target) errorMessageList.push(target);
    if (typeof e.reason === 'string') {
      errorMessageList.push(e.reason);
    } else {
      errorMessageList.push(JSON.stringify(e.reason));
    }
    $log.error(new Error(errorMessageList.join(' ')), $log.ERROR_TYPE.PROMISE);
  }, true);
  // 全局Vue未处理异常监听
  Vue.config.errorHandler = (error, vue, info) => {
    // eslint-disable-next-line no-console
    console.error('Vue Error:', error, vue, info);
    // 错误直接上报
    if (error instanceof Error) {
      $log.error(error, $log.ERROR_TYPE.VUE);
      return;
    }
    $log.error(new Error(`${error} ${vue.$vnode?.componentOptions?.tag ?? ''} ${info}`), $log.ERROR_TYPE.VUE);
  };

  // =#=#=#=#=#=#=#=#=#= Vue路由模块初始化 =#=#=#=#=#=#=#=#=#=
  Vue.use(Router);
  const router = hobbitRouter(window.$hobbit);

  const afterExtend = () => {
    // 在业务自定义扩展之后，已确定语言和国家了，应尽快先启动多语言资源和国家主题配置加载逻辑
    $resources.$installer.install($ctx.lang);
    $theme.$installer.install($ctx.biz);

    // =#=#=#=#=#=#=#=#=#= Vue全局引入 =#=#=#=#=#=#=#=#=#=
    Vue.mixin({
      beforeCreate() {
        // 安装多语言资源数据
        if (this.$options?.name) {
          this.res = this.$resources.computeRes(this.$ctx.revertLang ?? this.$ctx.lang, this.$options.name, this.$options.res);
        }

        // 页面额外增强逻辑
        if (this.$vnode?.data?.ref === 'page') {
          // 设置SEO信息
          setSEOInfo(this.res.$title || this.res.title, this.res.$keywords, this.res.$description);
        }
      },
      mounted() {
        if (this.$options?.name) {
          setComponentName(this, this.$options.name);
        }

        // 页面额外增强逻辑
        if (this.$vnode?.data?.ref === 'page' && typeof this.$log.autoSendPV === 'function' && this.$route.meta.pv) {
          // 页面自动上报页面PV
          this.$log.autoSendPV(this);
        }
      },
      activated() {
        // keep-alive模式下有可能改变了语言，则需要重新安装多语言资源数据
        if (this.$options?.name) {
          this.res = this.$resources.computeRes(this.$ctx.revertLang ?? this.$ctx.lang, this.$options.name, this.$options.res);
        }
      },
    });

    // =#=#=#=#=#=#=#=#=#= Vue实例化 =#=#=#=#=#=#=#=#=#=
    new Vue({
      router,
      render: h => h(appView),
      ...vueOptions,
    }).$mount('#hobbit_app');
  };

  // 执行业务自定义扩展初始化逻辑
  if (extend) {
    const extendResult = extend({ $hobbit: window.$hobbit, router });
    if (extendResult?.then) {
      extendResult.then(afterExtend);
    } else {
      afterExtend();
    }
  }
}
